import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, streamJob } from "../api/client";
import { Icon } from "./icons";
import { toast } from "./toast";

/**
 * What the script reader set aside, and a way to undo it.
 *
 * A marked-up script's notes, Word comments and crossed-out words aren't the
 * book's words, so they're kept out of the text the narration is compared
 * against (and kept as marks: flags on those words show them). This says how
 * much went, grouped by how it looks - red notes, Word comments... - and each
 * group has a switch to read it as the book's text after all: a coloured line
 * that really is the book's, say. Switching re-reads the script in the
 * background; files already proofed are then offered a re-proof.
 *
 * On the Project Hub's manuscript card (compact: one line, the list on request)
 * and above the text on the Review page.
 */

export interface MarkGroup {
  key: string;
  kind: "note" | "comment" | "struck";
  label: string;
  color: string;
  marks: number;
  words: number;
  pages: number[];
  examples: string[];
  kept: boolean;
}

export interface ScriptMarks {
  state: "none" | "checking" | "failed" | "rereading" | "clean" | "ready";
  words?: number;
  pages?: number;
  groups?: MarkGroup[];
  kept?: string[];
}

export function useScriptMarks(projectId: string | undefined) {
  return useQuery({
    queryKey: ["script-marks", projectId],
    queryFn: () => api<ScriptMarks>(`/api/modules/proofing/projects/${projectId}/script-marks`),
    refetchInterval: (q) => (["checking", "rereading"].includes(q.state.data?.state ?? "") ? 5000 : false),
    staleTime: 60_000,
    enabled: !!projectId,
  });
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

function pageList(pages: number[]): string {
  if (pages.length <= 6) return `${pages.length === 1 ? "p." : "pp."} ${pages.join(", ")}`;
  return `pp. ${pages.slice(0, 5).join(", ")} and ${pages.length - 5} more`;
}

function what(g: MarkGroup): string {
  if (g.kind === "comment") return plural(g.marks, "comment");
  if (g.kind === "struck") return plural(g.words, "word");
  return plural(g.marks, "note");
}

export function ScriptMarksSummary({ projectId, compact = false }: { projectId: string; compact?: boolean }) {
  const qc = useQueryClient();
  const { data } = useScriptMarks(projectId);
  const [open, setOpen] = useState(!compact);
  const [pending, setPending] = useState<string | null>(null);
  const stopWatching = useRef<(() => void) | null>(null);
  useEffect(() => () => stopWatching.current?.(), []);

  // A re-read that ended while nobody watched its job (the page was reloaded).
  const was = useRef(data?.state);
  useEffect(() => {
    if (was.current === "rereading" && data?.state && data.state !== "rereading") refreshAfterReread();
    was.current = data?.state;
  }, [data?.state]);  // eslint-disable-line react-hooks/exhaustive-deps

  function refreshAfterReread() {
    for (const key of ["script-marks", "manuscript-review", "script-check", "script-notes", "script-snippet", "result", "project"])
      qc.invalidateQueries({ queryKey: [key, projectId] });
  }

  async function toggle(g: MarkGroup) {
    setPending(g.key);
    try {
      const r = await api<{ kept: string[]; job_id: string | null }>(
        `/api/modules/proofing/projects/${projectId}/script-marks/keep`,
        { method: "PUT", body: JSON.stringify({ group: g.key, keep: !g.kept }) });
      qc.invalidateQueries({ queryKey: ["script-marks", projectId] });
      if (r.job_id) {
        toast(g.kept
          ? `${g.label} will be set aside again — re-reading the script.`
          : `${g.label} will be read as the book's text — re-reading the script.`, "info");
        stopWatching.current?.();
        stopWatching.current = streamJob(r.job_id, {
          onEnd: (m) => {
            const ok = (m?.status ?? "completed") === "completed";
            toast(ok ? "Script re-read. Files already proofed are offered a re-proof." : "Re-reading the script stopped — History has the details.",
              ok ? "success" : "error");
            refreshAfterReread();
          },
        });
      }
    } catch (e: any) {
      toast(e?.message || "Couldn't change that — please try again.", "error");
    } finally {
      setPending(null);
    }
  }

  // While a change is being applied the new reading has no list yet: keep
  // showing the last one, its switches waiting.
  const last = useRef<MarkGroup[]>([]);
  if (data?.groups?.length) last.current = data.groups;

  if (!data || data.state === "none" || data.state === "clean") return null;
  if (data.state === "checking")
    return <div className="sm-line" role="status"><span className="reproof-dot" /> Checking the script for notes, comments and crossed-out words…</div>;
  if (data.state === "failed")
    return <div className="sm-line quiet" role="status"><Icon name="alert" size={14} /> Couldn't check the script for notes just now — Mockingbird tries again in a few minutes.</div>;

  const groups = data.groups ?? (data.state === "rereading" ? last.current : []);
  const rereading = data.state === "rereading";
  const kept = groups.filter((g) => g.kept);
  const asideWords = data.words ?? 0;

  return (
    <div className={`sm-card${compact ? " compact" : ""}`}>
      <div className="sm-head">
        <Icon name="pen-line" size={15} className="sm-icon" />
        <div className="sm-text">
          {rereading ? (
            <><span className="reproof-dot" /> Re-reading the script with your change…</>
          ) : asideWords > 0 ? (
            <>
              <b>Set aside from the text: {plural(asideWords, "word")}</b> of the script's notes, comments and
              crossed-out words, on {plural(data.pages ?? 0, "page")}.
              {!compact && " They're kept as marks, not lost — a flag on those words shows them."}
            </>
          ) : (
            <><b>Nothing set aside:</b> every mark on the script is read as the book's text.</>
          )}
          {kept.length > 0 && !rereading && (
            <span className="sm-kept-note"> Read as text: {kept.map((g) => g.label.toLowerCase()).join(", ")}.</span>
          )}
        </div>
        {compact && (
          <button className="btn ghost sm sm-more" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? "Hide" : "What was set aside"} <Icon name={open ? "chevron-up" : "chevron-down"} size={14} />
          </button>
        )}
      </div>

      {open && (
        <ul className="sm-groups">
          {groups.map((g) => (
            <li key={g.key} className={`sm-group${g.kept ? " kept" : ""}`}>
              <span className="sm-swatch">
                {g.kind === "comment" ? <Icon name="message" size={14} />
                  : g.kind === "struck" ? <s className="sm-struck">ab</s>
                  : <span className="sm-dot" style={{ background: g.color }} />}
              </span>
              <div className="sm-body">
                <div className="sm-label">
                  {g.label} <span className="sm-meta">{what(g)} · {pageList(g.pages)}</span>
                </div>
                {g.examples.length > 0 && (
                  <div className="sm-examples">
                    {g.examples.map((x, i) => <span key={i}>{g.kind === "struck" ? <s>{x}</s> : /[“”"]/.test(x) ? x : `“${x}”`}</span>)}
                  </div>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={g.kept}
                className={`proof-toggle${g.kept ? " on" : ""}`}
                disabled={rereading || pending !== null}
                title={g.kept
                  ? `${g.label} are read as the book's text. Switch off to set them aside again.`
                  : `Read ${g.label.toLowerCase()} as the book's text — for marks that are really part of the book.`}
                onClick={() => toggle(g)}
              >
                <span className="proof-toggle-track"><span className="proof-toggle-knob" /></span>
                {pending === g.key ? "Saving…" : "Keep as text"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
